import React, {FC, useState} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../libs/shared/helpers/src/internationalization/react';
import {Handler, HandlerOf} from '../../../libs/shared/helpers/src/react/reactHelpers';
import {Button} from '../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {IconButton} from '../../../libs/shared/ui/src/components/buttons/iconButton';
import {Icon} from '../../../libs/shared/ui/src/components/icon/icon';
import {VisualSizesEnum} from '../../../libs/shared/ui/src/styles/commonStyles';
import {center} from '../../../libs/shared/ui/src/styles/mixins';
import {useUserTheme} from '../../../libs/shared/ui/src/theme/userThemeProvider';
import {InterfaceEventsEnum, trackInterfaceEvent} from '../../services/tracking';
import {CalendarEventData} from '../../store/panels/calendars/calendarsPanelSelectors';
import {CalendarEventGridParameters} from '../../store/panels/calendars/calendarsPanelState';
import {CalendarsPanelId} from '../../store/panels/panelStateBase';
import {SidePanelCloseButton} from '../inboxes/side/sidePanelCloseButton';
import {CalendarsEmptyContainer} from './empty/calendarsEmptyContainer';
import {CalendarsGridContainer} from './grid/calendarsGridContainer';
import {SideCalendarsHeader} from './header/sideCalendarsHeader';
import {CalendarsListContainer} from './list/calendarsListContainer';
import {SchedulingLinksListContainer} from './scheduling/schedulingLinksListContainer';
import {SideCalendarsControlsContainer} from './sideCalendarsControlsContainer';
import {SideCalendarEventViewerContainer} from './viewer/sideCalendarEventViewerContainer';

/*
 * Types.
 */

enum SideCalendarsOverlaysEnum {
  CALENDAR_EVENT,
  CALENDARS,
  SCHEDULING_LINKS,
}

/*
 * Props.
 */

interface SideCalendarsProps {
  panelId: CalendarsPanelId;
  isVisible: boolean;
  hasNoCalendars: boolean;
  focusedCalendarEventData: CalendarEventData | undefined;
  onCreateEventClick: Handler;
  onPopoutClick: Handler;
  onEventClick: HandlerOf<CalendarEventGridParameters>;
}

/*
 * Styles.
 */

const StyledContentDiv = styled.div`
  color: ${(p) => p.theme.text};
  grid-row: 2;
  display: flex;
  flex-direction: column;
  justify-content: stretch;
  width: 100%;
  height: 100%;
  position: relative;
  overflow: hidden;
`;

const StyledOverlayWrapperDiv = styled.div`
  position: absolute;
  height: 100%;
  width: 100%;
  top: 0px;
  left: 0px;
  overflow: hidden;

  display: flex;
  flex-direction: column;
  justify-content: stretch;

  background-color: ${(p) => p.theme.foreground};
`;

const StyledCalendarContentDiv = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: stretch;
  width: 100%;
  height: 100%;
  overflow: hidden;
`;

const StyledGridContainerDiv = styled.div`
  height: 100%;
  overflow: hidden;
  position: relative;
`;

const StyledCalendarListWrapperDiv = styled.div`
  overflow-y: scroll;
`;

const StyledGridHeaderDiv = styled.div`
  display: grid;
  grid-auto-columns: auto;
  grid-auto-flow: column;
  grid-column-gap: 4px;
  margin-right: -3px;
`;

const StyledSchedulingButtonWrapperDiv = styled.div`
  display: flex;
  border-top: 1px solid ${(p) => p.theme.greys.shade30};
  height: 65px;
  ${center()}
`;

const StyledSchedulingButtonContentWrapperDiv = styled.div`
  display: grid;
  grid-template-areas: 'icon text';
  align-items: center;
  grid-column-gap: 6px;
`;

const StyledSchedulingIcon = styled(Icon)`
  grid-area: icon;
  margin: -2px 0;
`;

const StyledSchedulingTextDiv = styled.div`
  grid-area: text;
`;

/*
 * Component.
 */

export const SideCalendars: FC<SideCalendarsProps> = (props) => {
  const theme = useUserTheme();
  const {hasNoCalendars, isVisible, onEventClick} = props;
  const [visibleOverlay, setVisibleOverlay] = useState(SideCalendarsOverlaysEnum.CALENDAR_EVENT);

  /*
   * Render.
   */

  const titleRender = <Trans id="calendar-header-calendar">Calendar</Trans>;

  const renderSchedulingButton = () => (
    <StyledSchedulingButtonWrapperDiv>
      <Button
        variant={ButtonVariantsEnum.SECONDARY}
        size={VisualSizesEnum.MEDIUM}
        onClick={() => setVisibleOverlay(SideCalendarsOverlaysEnum.SCHEDULING_LINKS)}
        interactionId="sidecalendar-view-schedulinglinks"
      >
        <StyledSchedulingButtonContentWrapperDiv>
          <StyledSchedulingIcon name="schedule" />
          <StyledSchedulingTextDiv>
            <Trans id="view-schedulinglinks">View scheduling links</Trans>
          </StyledSchedulingTextDiv>
        </StyledSchedulingButtonContentWrapperDiv>
      </Button>
    </StyledSchedulingButtonWrapperDiv>
  );

  const renderContent = () => {
    if (hasNoCalendars) {
      return <CalendarsEmptyContainer />;
    }

    return (
      <>
        <SideCalendarsControlsContainer panelId={panelId} />
        <StyledGridContainerDiv>
          <CalendarsGridContainer
            panelId={panelId}
            isVisible={isVisible}
            popoverCalendarEventData={undefined}
            onEventClick={onEventClick}
            canCreateEventOnSelect
            shouldCalendarDisplayWeekends
          />
        </StyledGridContainerDiv>
        {renderSchedulingButton()}
      </>
    );
  };

  const {panelId, onCreateEventClick, onPopoutClick} = props;
  return (
    <StyledContentDiv>
      <SideCalendarsHeader title={titleRender}>
        <StyledGridHeaderDiv>
          {!hasNoCalendars && (
            <IconButton
              iconName="new"
              label={<Trans id="calendar-header-newevent">New event</Trans>}
              onClick={onCreateEventClick}
              color={theme.greys.shade80}
              hoverColor={theme.greys.shade80}
            />
          )}
          {!hasNoCalendars && (
            <IconButton
              iconName="calendars"
              label={<Trans id="calendar-header-selectcalendars">Select calendars</Trans>}
              onClick={() => {
                setVisibleOverlay(SideCalendarsOverlaysEnum.CALENDARS);
                trackInterfaceEvent(InterfaceEventsEnum.OPEN_CALENDAR_LIST);
              }}
              color={theme.greys.shade80}
              hoverColor={theme.greys.shade80}
            />
          )}
          <IconButton
            iconName="popout"
            label={<Trans id="calendar-header-popout">Open calendar popup</Trans>}
            onClick={() => {
              onPopoutClick();
              trackInterfaceEvent(InterfaceEventsEnum.POPOUT_CALENDAR);
            }}
            color={theme.greys.shade80}
            hoverColor={theme.greys.shade80}
          />
          <SidePanelCloseButton />
        </StyledGridHeaderDiv>
      </SideCalendarsHeader>
      <StyledCalendarContentDiv>{renderContent()}</StyledCalendarContentDiv>
      <SideCalendarsOverlay
        {...props}
        visibleOverlay={visibleOverlay}
        setVisibleOverlay={setVisibleOverlay}
      />
    </StyledContentDiv>
  );
};

/*
 * Overlay.
 */

interface SideCalendarsOverlayProps extends SideCalendarsProps {
  visibleOverlay: SideCalendarsOverlaysEnum;
  setVisibleOverlay: HandlerOf<SideCalendarsOverlaysEnum>;
}

const SideCalendarsOverlay: FC<SideCalendarsOverlayProps> = (props) => {
  const {panelId, focusedCalendarEventData, visibleOverlay, setVisibleOverlay} = props;

  switch (visibleOverlay) {
    case SideCalendarsOverlaysEnum.CALENDARS:
      return (
        <StyledOverlayWrapperDiv>
          <SideCalendarsHeader
            title={<Trans id="calendar-header-selectcalendars">Select calendars</Trans>}
            onBackClick={() => setVisibleOverlay(SideCalendarsOverlaysEnum.CALENDAR_EVENT)}
          />
          <StyledCalendarListWrapperDiv>
            <CalendarsListContainer
              panelId={panelId}
              shouldCombineCalendarsList={false}
              shouldSaveCalendarVersion
            />
          </StyledCalendarListWrapperDiv>
        </StyledOverlayWrapperDiv>
      );

    case SideCalendarsOverlaysEnum.SCHEDULING_LINKS:
      return (
        <StyledOverlayWrapperDiv>
          <SchedulingLinksListContainer
            panelId={panelId}
            onBackClick={() => setVisibleOverlay(SideCalendarsOverlaysEnum.CALENDAR_EVENT)}
          />
        </StyledOverlayWrapperDiv>
      );

    case SideCalendarsOverlaysEnum.CALENDAR_EVENT:
      if (focusedCalendarEventData) {
        return (
          <StyledOverlayWrapperDiv>
            <SideCalendarEventViewerContainer
              panelId={panelId}
              calendarEventData={focusedCalendarEventData}
            />
          </StyledOverlayWrapperDiv>
        );
      }
      return null;

    default:
      return null;
  }
};
